import { makeStyles, Theme } from '@material-ui/core';
import React, { MutableRefObject, ReactNode } from 'react';
import colorPalette from '../themes/colorPalette';
import clsx from 'clsx';

const { grey, hyperlinkBlue, primary } = colorPalette;

type TypoProps = {
  innerRef?: MutableRefObject<HTMLHeadingElement | HTMLParagraphElement>;
  variant: TypographyVariantTypes;
  fontWeight?: TypographyFontWeightTypes;
  color?: TypographyColorTypes;
  children: ReactNode;
  className?: string;
};

export const typographyVariantTypes = {
  title1: 'title1',
  title2: 'title2',
  bodyLg: 'bodyLg',
  bodyMd: 'bodyMd',
  bodySm: 'bodySm',
};

export type TypographyVariantTypes =
  | 'title1'
  | 'title2'
  | 'bodyLg'
  | 'bodyMd'
  | 'bodySm';

export type TypographyColorTypes = 'default' | 'muted' | 'active' | 'link';

export const typographyColorTypes = {
  default: 'default',
  muted: 'muted',
  active: 'active',
  link: 'link',
};

export type TypographyFontWeightTypes = 'default' | 'medium' | 'bold';

export const typographyFontWeightTypes = {
  default: 'default',
  medium: 'medium',
  bold: 'bold',
};

type StylesProps = {
  color: TypographyColorTypes;
  fontWeight: TypographyFontWeightTypes;
};
const useStyles = makeStyles<Theme, StylesProps>({
  title1: {
    fontWeight: 'bold',
    fontSize: '22px',
    letterSpacing: '0.4px',
    lineHeight: '28px',
    color: ({ color }) => getColor(color),
  },
  title2: {
    fontWeight: 'bold',
    fontSize: '21px',
    letterSpacing: '0.32px',
    lineHeight: '28px',
    color: ({ color }) => getColor(color),
  },
  bodyLg: {
    fontWeight: ({ fontWeight }) => getFontWeight(fontWeight),
    fontSize: '19px',
    letterSpacing: ({ fontWeight, color }) =>
      getLetterSpacingForBodyLg(color, fontWeight),
    lineHeight: '24px',
    color: ({ color }) => getColor(color),
  },
  bodyMd: {
    fontWeight: ({ fontWeight }) => getFontWeight(fontWeight),
    fontSize: '17px',
    letterSpacing: ({ fontWeight, color }) =>
      getLetterSpacingForBodyMd(color, fontWeight),
    lineHeight: '24px',
    color: ({ color }) => getColor(color),
  },
  bodySm: {
    fontWeight: ({ fontWeight }) => getFontWeight(fontWeight),
    fontSize: '15px',
    lineHeight: '20px',
    letterSpacing: ({ fontWeight }) => getLetterSpacingForBodySm(fontWeight),
    color: ({ color }) => getColor(color),
  },
  all: {
    margin: 0,
    padding: 0,
  },
});

function Typo({
  variant,
  fontWeight = 'default',
  color = 'default',
  children,
  innerRef,
  className,
  ...other
}: TypoProps) {
  const classes = useStyles({ fontWeight, color });
  const classNames = clsx(classes[variant], classes.all, className);

  switch (variant) {
    case 'title1':
      return (
        <h1 ref={innerRef} className={classNames} {...other}>
          {children}
        </h1>
      );
    case 'title2':
      return (
        <h2 ref={innerRef} className={classNames} {...other}>
          {children}
        </h2>
      );
    case 'bodyLg':
      return (
        <h3 ref={innerRef} className={classNames} {...other}>
          {children}
        </h3>
      );
    default:
      return (
        <p ref={innerRef} className={classNames} {...other}>
          {children}
        </p>
      );
  }
}

function getColor(color: TypographyColorTypes) {
  switch (color) {
    case 'muted':
      return grey[8];
    case 'active':
      return primary.dark;
    case 'link':
      return hyperlinkBlue;
    default:
      return grey[10];
  }
}

function getFontWeight(fontWeight: TypographyFontWeightTypes) {
  switch (fontWeight) {
    case 'medium':
      return 500;
    case 'bold':
      return 'bold';
    default:
      return 'normal';
  }
}

function getLetterSpacingForBodyLg(
  color: TypographyColorTypes,
  fontWeight: TypographyFontWeightTypes
) {
  if (color === 'default' && fontWeight === 'medium') return '0.15px';
  return '0.1px';
}

function getLetterSpacingForBodyMd(
  color: TypographyColorTypes,
  fontWeight: TypographyFontWeightTypes
) {
  if ((color === 'active' || color === 'muted') && fontWeight === 'medium')
    return '0.12px';
  if (color === 'default' && fontWeight === 'medium') return '0px';
  return '0.1px';
}

function getLetterSpacingForBodySm(fontWeight: TypographyFontWeightTypes) {
  if (fontWeight === 'bold' || fontWeight === 'medium') return '0.15px';
  return '0.1px';
}

export default Typo;
